import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { EstimateDetailResponse } from './contractsMirror.js';

/**
 * TanStack Query hook for the COARSE estimate-detail endpoint
 * (`GET /api/v1/estimates/:id`, 21-05). One query key per estimate id.
 *
 * The detail query leaves the default staleTime — the status badge reflects the
 * latest server value on each load (the server derives status read-only; D-02
 * approval auto-converts the estimate to a job server-side, and a refetch picks
 * up the new `converted` status + the `convertedJobName`).
 *
 * The request goes through `apiFetch` (the only fetch path); the SPA never sends
 * a tenant id (SEC-11) — the server scopes by the auth principal.
 */
export function estimateQueryKey(id: number): readonly unknown[] {
  return ['estimate', id] as const;
}

export function useEstimateQuery(id: number, enabled = true) {
  return useQuery<EstimateDetailResponse>({
    queryKey: estimateQueryKey(id),
    queryFn: () => apiFetchJson<EstimateDetailResponse>(`/api/v1/estimates/${id}`),
    // `enabled` lets the edit form skip the fetch in create mode (id is absent),
    // while the detail route keeps the default (always fetch).
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}
